import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useMissions } from "../contexts/MissionsContext";
import { SFX } from "../utils/audio";
import { ArrowLeft, Calendar, CalendarDays, CalendarRange, Check, Gift, LogIn } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = [
  { key: "daily", label: "Quotidiennes", sub: "Renouvelées chaque jour", icon: Calendar, color: "text-cyan-300", accent: "bg-cyan-500" },
  { key: "weekly", label: "Hebdomadaires", sub: "Renouvelées chaque semaine", icon: CalendarDays, color: "text-fuchsia-300", accent: "bg-fuchsia-500" },
  { key: "monthly", label: "Mensuelles", sub: "Renouvelées chaque mois", icon: CalendarRange, color: "text-amber-300", accent: "bg-amber-500" },
];

const MissionCard = ({ mission, onClaim, claiming }) => {
  const pct = Math.min(100, Math.round((mission.progress / mission.target) * 100));
  const done = mission.completed;
  const claimed = mission.claimed;
  return (
    <div
      data-testid={`mission-${mission.id}`}
      className={`bg-slate-800 border-4 border-slate-950 rounded-2xl p-4 cartoon-shadow-static transition-all ${claimed ? "opacity-60" : ""}`}
    >
      <div className="flex items-center gap-3">
        <div className="text-3xl shrink-0">{mission.icon}</div>
        <div className="flex-1 min-w-0">
          <p className="font-display font-bold text-slate-50 text-sm leading-tight">{mission.label}</p>
          <p className="text-xs text-slate-400 mt-0.5">
            🪙 {mission.reward.gold} · ✨ {mission.reward.xp} XP
          </p>
        </div>
        {claimed ? (
          <span className="shrink-0 inline-flex items-center gap-1 text-emerald-400 text-xs font-bold">
            <Check className="w-4 h-4" /> Reçu
          </span>
        ) : done ? (
          <button
            data-testid={`claim-mission-${mission.id}`}
            disabled={claiming}
            onClick={() => onClaim(mission)}
            className="shrink-0 inline-flex items-center gap-1.5 bg-emerald-500 text-slate-950 font-extrabold text-xs uppercase tracking-wide px-3 py-2 rounded-xl border-2 border-emerald-700 hover:bg-emerald-400 transition-colors disabled:opacity-50"
          >
            <Gift className="w-4 h-4" /> Réclamer
          </button>
        ) : (
          <span className="shrink-0 text-xs font-bold text-slate-300 tabular-nums">
            {mission.progress}/{mission.target}
          </span>
        )}
      </div>
      {!done && (
        <div className="mt-3 h-2.5 bg-slate-950 rounded-full overflow-hidden border border-slate-700">
          <div className="h-full bg-cyan-400 rounded-full transition-all" style={{ width: `${pct}%` }} />
        </div>
      )}
    </div>
  );
};

export default function Missions() {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const { missions, loading, claim } = useMissions();
  const [claiming, setClaiming] = useState(null);

  const handleClaim = async (mission) => {
    setClaiming(mission.id);
    try {
      const res = await claim(mission.category, mission.id);
      SFX.click?.();
      toast.success("Récompense récupérée !", {
        description: `+${res.reward.gold} 🪙 et +${res.reward.xp} XP`,
        duration: 3000,
      });
      refreshUser?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible de réclamer cette mission");
    } finally {
      setClaiming(null);
    }
  };

  return (
    <div data-testid="missions-page" className="min-h-screen bg-slate-950 text-slate-100 px-4 py-6">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <button
            data-testid="missions-back-btn"
            onClick={() => { SFX.click?.(); navigate("/"); }}
            className="inline-flex items-center gap-2 bg-slate-800 border-4 border-slate-950 rounded-xl px-3 py-2 font-bold text-sm cartoon-shadow-static hover:bg-slate-700 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Menu
          </button>
          <h1 className="font-display text-3xl font-extrabold text-slate-50">Missions</h1>
        </div>

        {!user ? (
          <div data-testid="missions-login-gate" className="text-center bg-slate-900 border-4 border-slate-800 rounded-2xl p-10 cartoon-shadow-static">
            <LogIn className="w-10 h-10 mx-auto text-cyan-400 mb-3" />
            <p className="font-display text-lg text-slate-100 mb-1">Connecte-toi pour suivre tes missions</p>
            <p className="text-sm text-slate-400">Les missions et leurs récompenses sont réservées aux comptes connectés (sauvegarde cloud).</p>
          </div>
        ) : loading && !missions ? (
          <p className="text-center text-slate-400 py-10">Chargement des missions…</p>
        ) : (
          <div className="space-y-8">
            {CATEGORIES.map((cat) => {
              const Icon = cat.icon;
              const list = missions?.[cat.key]?.missions || [];
              return (
                <section key={cat.key} data-testid={`missions-section-${cat.key}`}>
                  <div className="flex items-center gap-2 mb-3">
                    <div className={`w-9 h-9 rounded-xl ${cat.accent} border-2 border-slate-950 flex items-center justify-center`}>
                      <Icon className="w-5 h-5 text-slate-950" />
                    </div>
                    <div>
                      <h2 className={`font-display font-bold text-lg ${cat.color}`}>{cat.label}</h2>
                      <p className="text-xs text-slate-500">{cat.sub}</p>
                    </div>
                  </div>
                  <div className="grid gap-3">
                    {list.length === 0 ? (
                      <p className="text-sm text-slate-500">Aucune mission active.</p>
                    ) : (
                      list.map((m) => (
                        <MissionCard key={m.id} mission={m} onClaim={handleClaim} claiming={claiming === m.id} />
                      ))
                    )}
                  </div>
                </section>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
